import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays, Sparkles, ShoppingBag } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import SEO from "@/components/SEO";
import { FESTIVALS_2026, MONTH_NAMES, HINDU_MONTHS, festivalsForMonth, festivalsForDate, upcomingFestivals } from "@/data/panchang";

const TYPE_STYLES = {
  major:     { bg: "#C89D3C", label: "Major Festival" },
  ekadashi:  { bg: "#1D524D", label: "Ekadashi" },
  purnima:   { bg: "#8A1A2B", label: "Purnima" },
  amavasya:  { bg: "#1B2A49", label: "Amavasya" },
  vrat:      { bg: "#E06B22", label: "Vrat" },
  sankashti: { bg: "#73232A", label: "Sankashti" },
  national:  { bg: "#3B2A1A", label: "National" },
};

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function startWeekday(year, month) {
  return new Date(year, month, 1).getDay(); // 0=Sun
}
function fmt(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function MonthGrid({ year, month, onSelect, selected }) {
  const total = daysInMonth(year, month);
  const start = startWeekday(year, month);
  const cells = [];
  for (let i = 0; i < start; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);

  const today = new Date();
  const isToday = (d) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  return (
    <div>
      <div className="grid grid-cols-7 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969] text-center py-2 font-semibold">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-[#3B2A1A]/10 border border-[#3B2A1A]/10">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="bg-[#FBF6EC] aspect-square" />;
          const dateStr = fmt(year, month, d);
          const fests = festivalsForDate(dateStr);
          const hasFest = fests.length > 0;
          const primary = hasFest ? fests[0] : null;
          const style = primary ? TYPE_STYLES[primary.type] || TYPE_STYLES.vrat : null;
          const active = selected === dateStr;
          return (
            <button
              key={i}
              onClick={() => onSelect(dateStr)}
              data-testid={`cal-day-${dateStr}`}
              className={`bg-white aspect-square p-1.5 sm:p-2 flex flex-col items-start justify-between text-left transition-colors ${active ? "ring-2 ring-[#C89D3C] ring-inset" : "hover:bg-[#FBF6EC]/40"}`}
            >
              <span className={`text-sm sm:text-base font-serif-display ${isToday(d) ? "text-[#C89D3C] font-semibold" : "text-[#3B2A1A]"}`}>{d}</span>
              {hasFest && (
                <span className="w-full">
                  <span className="block w-full h-1 rounded-sm" style={{ background: style.bg }} />
                  <span className="hidden sm:block text-[9px] leading-tight mt-1 text-[#5C4A3A] line-clamp-2">{primary.name}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const Calendar = () => {
  const now = new Date();
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(Math.min(now.getMonth(), 11));
  const [selected, setSelected] = useState(null);

  const monthFestivals = useMemo(() => festivalsForMonth(year, month), [year, month]);
  const selectedFestivals = selected ? festivalsForDate(selected) : [];
  const hindu = HINDU_MONTHS[month];

  const nextMonth = () => setMonth((m) => (m === 11 ? 0 : m + 1));
  const prevMonth = () => setMonth((m) => (m === 0 ? 11 : m - 1));

  return (
    <div data-testid="calendar-page">
      <SEO
        title="Hindu Festival Calendar 2026 — Sanatan Dharma"
        description="Complete Hindu festival calendar for 2026. All major Hindu festivals, ekadashis, purnimas, vrats and auspicious days — Diwali, Holi, Navratri, Mahashivratri, Janmashtami, Karva Chauth and more."
        keywords="hindu calendar 2026, hindu festival calendar, panchang 2026, ekadashi dates 2026, diwali 2026 date, holi 2026, navratri 2026, mahashivratri 2026, karva chauth 2026, auspicious days 2026, hindu panchang"
        path="/calendar"
      />

      <section className="mandala-bg border-b border-[#3B2A1A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <SectionHeading
            overline="Hindu Panchang 2026"
            title="A Year Of Festivals & Auspicious Days."
            subtitle="Every major Hindu festival, ekadashi, purnima and vrat for 2026 — with the deity to worship, exact date and the samagri you'll need."
          />
        </div>
      </section>

      <section className="py-10 lg:py-12 bg-[#F5EFE3] border-b border-[#3B2A1A]/10" data-testid="upcoming-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <p className="overline">Upcoming — Next 6</p>
            <Link to="/kits" className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#C89D3C] hover:text-[#3B2A1A]" data-testid="upcoming-kits-link">
              Shop Kits →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {upcomingFestivals(new Date(), 6).map((f) => {
              const style = TYPE_STYLES[f.type] || TYPE_STYLES.vrat;
              const d = new Date(f.date);
              return (
                <button
                  key={f.name + f.date}
                  onClick={() => { setMonth(d.getMonth()); setSelected(f.date); }}
                  data-testid={`upcoming-${f.date}`}
                  className="text-left bg-white border border-[#3B2A1A]/10 p-4 hover:border-[#C89D3C] transition-colors group"
                >
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif-display text-3xl" style={{ color: style.bg }}>{d.getDate()}</span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A7969] font-semibold">
                      {MONTH_NAMES[d.getMonth()].slice(0, 3)}
                    </span>
                  </div>
                  <p className="text-sm font-serif-display text-[#3B2A1A] mt-2 leading-tight line-clamp-2 min-h-[2.5em]">{f.name}</p>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A7969] mt-1 font-semibold">{style.label}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="overline">Viewing</p>
                <h2 className="font-serif-display text-4xl text-[#3B2A1A] leading-tight mt-1" data-testid="calendar-month-heading">
                  {MONTH_NAMES[month]} {year}
                </h2>
                <p className="text-sm text-[#8A7969] mt-1 font-light">Traditional month: {hindu.name}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={prevMonth} data-testid="cal-prev-btn"
                  className="p-3 border border-[#3B2A1A]/20 text-[#3B2A1A] hover:border-[#C89D3C] hover:text-[#C89D3C] transition-colors">
                  <ChevronLeft size={16} />
                </button>
                <button onClick={nextMonth} data-testid="cal-next-btn"
                  className="p-3 border border-[#3B2A1A]/20 text-[#3B2A1A] hover:border-[#C89D3C] hover:text-[#C89D3C] transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <MonthGrid year={year} month={month} onSelect={setSelected} selected={selected} />

            <div className="mt-8 flex flex-wrap gap-4">
              {Object.entries(TYPE_STYLES).map(([type, cfg]) => (
                <div key={type} className="flex items-center gap-2 text-xs text-[#5C4A3A]">
                  <span className="w-3 h-3 rounded-sm" style={{ background: cfg.bg }} />
                  {cfg.label}
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-6">
            {selected && selectedFestivals.length > 0 && (
              <div className="bg-white border border-[#3B2A1A]/10 p-6" data-testid="selected-festival-panel">
                <p className="overline">Selected Date</p>
                <p className="font-serif-display text-2xl text-[#3B2A1A] mt-2 leading-tight">
                  {new Date(selected).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </p>
                {selectedFestivals.map((f) => {
                  const style = TYPE_STYLES[f.type] || TYPE_STYLES.vrat;
                  return (
                    <div key={f.name} className="mt-5 border-t border-[#3B2A1A]/10 pt-5">
                      <span className="text-[9px] uppercase tracking-[0.24em] font-semibold" style={{ color: style.bg }}>{style.label}</span>
                      <p className="font-serif-display text-xl text-[#3B2A1A] mt-1 leading-tight">{f.name}</p>
                      <p className="text-sm text-[#5C4A3A] mt-2 font-light leading-relaxed">{f.description}</p>

                      {f.deity && f.deity !== "—" && (
                        <div className="mt-4 flex items-start gap-2" data-testid={`deity-${f.name}`}>
                          <Sparkles size={13} className="text-[#C89D3C] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7969]">Worship</p>
                            <p className="text-sm font-serif-display text-[#3B2A1A] mt-0.5">{f.deity}</p>
                          </div>
                        </div>
                      )}

                      {f.items && f.items.length > 0 && (
                        <div className="mt-4" data-testid={`items-${f.name}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <ShoppingBag size={13} className="text-[#C89D3C]" />
                            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7969]">What To Buy</p>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {f.items.map((it) => (
                              <span key={it} className="text-[11px] bg-[#F5EFE3] text-[#3B2A1A] px-2.5 py-1 border border-[#3B2A1A]/10">{it}</span>
                            ))}
                          </div>
                          <Link to="/products"
                            className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C89D3C] hover:text-[#3B2A1A]"
                            data-testid="festival-shop-cta">
                            Shop these essentials →
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="bg-[#F5EFE3] border border-[#3B2A1A]/10 p-6" data-testid="month-festivals-list">
              <p className="overline mb-4">This Month <CalendarDays size={12} className="inline align-baseline text-[#C89D3C] ml-1" /></p>
              {monthFestivals.length === 0 ? (
                <p className="text-sm text-[#8A7969] font-light">No major festivals this month.</p>
              ) : (
                <ul className="divide-y divide-[#3B2A1A]/10">
                  {monthFestivals.map((f) => {
                    const style = TYPE_STYLES[f.type] || TYPE_STYLES.vrat;
                    const d = new Date(f.date);
                    return (
                      <li key={f.name + f.date} className="py-3">
                        <button
                          onClick={() => setSelected(f.date)}
                          data-testid={`month-fest-${f.date}-${f.name}`}
                          className="w-full text-left flex gap-4 hover:bg-white/50 -mx-2 px-2 py-1 transition-colors"
                        >
                          <div className="text-center w-12 flex-shrink-0">
                            <p className="font-serif-display text-2xl leading-none" style={{ color: style.bg }}>{d.getDate()}</p>
                            <p className="text-[9px] uppercase tracking-[0.18em] text-[#8A7969] mt-1">{MONTH_NAMES[d.getMonth()].slice(0, 3)}</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-serif-display text-[#3B2A1A] leading-tight">{f.name}</p>
                            <p className="text-[11px] text-[#8A7969] mt-0.5">{style.label}{f.deity && f.deity !== "—" ? ` · ${f.deity}` : ""}</p>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="bg-[#3B2A1A] text-[#FBF6EC] p-6">
              <p className="overline text-[#E2C37B]">Stock Up</p>
              <p className="font-serif-display text-2xl mt-2 leading-tight">Every festival, ratioed & ready.</p>
              <p className="text-sm text-[#FBF6EC]/80 mt-3 font-light leading-relaxed">
                From Diwali to Navratri to Griha Pravesh — our curated kits contain every item you need with a step-by-step guide.
              </p>
              <Link to="/kits" className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#E2C37B] hover:text-white" data-testid="cal-kits-cta">
                Browse Pooja Kits →
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Calendar;
